import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSegment from '@/hooks/useSegment';
import { educationTopic } from '@/patient-app-common/helpers/education_helpers';
import { Education } from '@/patient-app-common/types/Education';

interface EduFeedbackProps {
  education: Education[];
  topicId: string;
}

type EduFeedbackParams = {
  id: number;
  title: string[];
  procedureID: number;
  feedback?: string;
};

const EVENT_THUMBS_UP = 'Education Feedback Thumbs Up';
const EVENT_THUMBS_DOWN = 'Education Feedback Thumbs Down';

export function EduFeedback({ education, topicId }: EduFeedbackProps) {
  const { t } = useTranslation('education');
  const { sendTrackEvent } = useSegment();

  const [data, setData] = useState<EduFeedbackParams | null>(null);
  const [selectedBtn, setSelectedBtn] = useState('');
  const [modal, toggleModal] = useState(false);
  const [comment, setComment] = useState('');

  const topic = useMemo(() => {
    if (!topicId) return null;
    return educationTopic(education, topicId);
  }, [education, topicId]);

  useEffect(() => {
    if (!topic) return;

    setSelectedBtn('');

    setData({
      id: topic.id,
      title: [topic.title],
      procedureID: topic.procedureId,
    });
  }, [topic]);

  const submitPositiveFeedback = () => {
    setSelectedBtn('yes');
    sendTrackEvent(EVENT_THUMBS_UP, { ...data });
  };

  const submitNegativeFeedback = () => {
    toggleModal(false);
    sendTrackEvent(EVENT_THUMBS_DOWN, { ...data, comment });
  };

  if (!topic?.html) return null;

  return (
    <>
      <Modal isOpen={modal} onClose={submitNegativeFeedback}>
        <ModalContent>
          <ModalHeader>{t('edufeedback.form.title')}</ModalHeader>
          <ModalBody>
            <Textarea
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('common.input.text_placeholder')}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={submitNegativeFeedback}>
              {t('common.action.submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <div className="flex flex-col items-center py-4">
        <p>{t('edufeedback.title')}</p>
        <div className="p-4 space-x-2 inline-flex">
          <Button
            color="primary"
            endContent={
              <Icon icon="mdi:thumb-up-outline" className="text-lg" />
            }
            onPress={submitPositiveFeedback}
            variant={selectedBtn === 'yes' ? 'solid' : 'faded'}
          >
            {t('edufeedback.button.helpful')}
          </Button>
          <Button
            color="primary"
            endContent={
              <Icon icon="mdi:thumb-down-outline" className="text-lg" />
            }
            onPress={() => {
              setSelectedBtn('no');
              toggleModal(true);
            }}
            variant={selectedBtn === 'no' ? 'solid' : 'faded'}
          >
            {t('edufeedback.button.not_helpful')}
          </Button>
        </div>
      </div>
    </>
  );
}
